<template>
  <div class="model-viewer">
    <div class="three-container" ref="containerRef">
      <div v-if="loading" class="loading-indicator">
        <div class="loading-spinner"></div>
        <p>載入數位人模型中...</p>
      </div>
    </div>
    <div class="speaking-indicator" v-if="action === 'Talking'">
      <div class="wave"></div>
      <div class="wave"></div>
      <div class="wave"></div>
    </div>
    <div class="status-text">
      {{ statusText }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick, markRaw } from 'vue'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { glbTestSuite } from '../utils/glbTest.js'

const props = defineProps({
  modelUrl: {
    type: String,
    default: '/models/default.glb'
  },
  emotion: {
    type: String,
    default: ''
  },
  action: {
    type: String,
    default: 'Idle'
  }
})

const containerRef = ref(null)
const loading = ref(true)
const scene = ref(null)
const camera = ref(null)
const renderer = ref(null)
const model = ref(null)
const mixer = ref(null)
const clock = ref(markRaw(new THREE.Clock()))

const statusText = computed(() => {
  if (loading.value) {
    return '載入中...'
  }
  if (props.action === 'Talking') {
    return '正在說話...'
  }
  if (props.emotion === 'cheerful') {
    return '心情愉快'
  }
  if (props.emotion === 'sad') {
    return '心情沉重'
  }
  if (props.emotion === 'assistant') {
    return '準備幫助'
  }
  return '準備就緒'
})

// Initialize Three.js scene
async function initThreeScene() {
  try {
    await nextTick()
    
    if (!containerRef.value) {
      console.error('❌ Container ref not available')
      return
    }

    console.log('🎬 開始初始化Three.js場景...')
    console.log('📦 Three.js版本:', THREE.REVISION)
    console.log('📐 容器尺寸:', {
      width: containerRef.value.clientWidth,
      height: containerRef.value.clientHeight
    })
    
    // 验证容器尺寸
    if (containerRef.value.clientWidth === 0 || containerRef.value.clientHeight === 0) {
      console.warn('⚠️ 容器尺寸为零，可能影响渲染')
    }

    // Scene setup - use markRaw to prevent Vue reactivity
    scene.value = markRaw(new THREE.Scene())
    scene.value.background = new THREE.Color(0xf3f8f9) // Match website background

    // Camera setup - use markRaw to prevent Vue reactivity
    camera.value = markRaw(new THREE.PerspectiveCamera(
      75,
      containerRef.value.clientWidth / containerRef.value.clientHeight,
      0.1,
      1000
    ))
    camera.value.position.set(0, 0, 3)

    // Renderer setup - use markRaw to prevent Vue reactivity
    renderer.value = markRaw(new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true 
    }))
    renderer.value.setSize(containerRef.value.clientWidth, containerRef.value.clientHeight)
    renderer.value.setPixelRatio(window.devicePixelRatio)
    renderer.value.shadowMap.enabled = true
    renderer.value.shadowMap.type = THREE.PCFSoftShadowMap
    renderer.value.outputColorSpace = THREE.SRGBColorSpace
    containerRef.value.appendChild(renderer.value.domElement)

    // Lighting setup - use markRaw for all Three.js objects
    const ambientLight = markRaw(new THREE.AmbientLight(0xffffff, 0.6))
    scene.value.add(ambientLight)

    const directionalLight = markRaw(new THREE.DirectionalLight(0xffffff, 0.8))
    directionalLight.position.set(1, 1, 0.5)
    directionalLight.castShadow = true
    directionalLight.shadow.camera.near = 0.1
    directionalLight.shadow.camera.far = 500
    directionalLight.shadow.camera.left = -10
    directionalLight.shadow.camera.right = 10
    directionalLight.shadow.camera.top = 10
    directionalLight.shadow.camera.bottom = -10
    scene.value.add(directionalLight)

    // Load GLB model
    await loadModel()
    
    // Start render loop
    animate()
    
    loading.value = false
    console.log('✅ 3D數位人模型載入完成')
    
  } catch (error) {
    console.error('❌ 3D場景初始化失敗:', error)
    loading.value = false
  }
}

// Load the GLB model
async function loadModel() {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader()
    
    console.log('🔄 開始載入3D模型:', props.modelUrl)
    console.log('📂 GLTFLoader创建成功')
    
    loader.load(
      props.modelUrl,
      (gltf) => {
        console.log('📦 GLB文件加載成功:', {
          scene: gltf.scene ? '✅' : '❌',
          animations: gltf.animations ? gltf.animations.length : 0,
          cameras: gltf.cameras ? gltf.cameras.length : 0,
          scenes: gltf.scenes ? gltf.scenes.length : 0
        })
        
        model.value = markRaw(gltf.scene)
        
        if (!model.value) {
          throw new Error('GLB場景為空')
        }
        
        // 獲取模型邊界框來正確縮放
        const box = new THREE.Box3().setFromObject(model.value)
        const size = box.getSize(new THREE.Vector3())
        const center = box.getCenter(new THREE.Vector3())
        
        console.log('📏 模型資訊:', {
          size: { x: size.x.toFixed(2), y: size.y.toFixed(2), z: size.z.toFixed(2) },
          center: { x: center.x.toFixed(2), y: center.y.toFixed(2), z: center.z.toFixed(2) }
        })
        
        // 自動調整模型縮放以適應視窗
        const maxDimension = Math.max(size.x, size.y, size.z)
        const scale = maxDimension > 0 ? 2 / maxDimension : 1
        
        console.log('🔢 應用縮放系數:', scale)
        
        model.value.scale.setScalar(scale)
        model.value.position.set(-center.x * scale, -center.y * scale - 0.5, -center.z * scale)
        
        // Enable shadows with safety checks and count meshes
        let meshCount = 0
        let materialCount = 0
        model.value.traverse((child) => {
          console.log('🧮 遍歷子物件:', {
            type: child.type,
            name: child.name || 'unnamed',
            isMesh: child.isMesh,
            hasGeometry: !!child.geometry,
            hasMaterial: !!child.material
          })
          
          if (child && child.isMesh) {
            meshCount++
            if (child.material) {
              materialCount++
              // 確保材質可見
              if (child.material.transparent === undefined) {
                child.material.transparent = false
              }
              if (child.material.opacity === undefined) {
                child.material.opacity = 1.0
              }
            }
            try {
              child.castShadow = true
              child.receiveShadow = true
            } catch (error) {
              console.warn('陰影設置警告:', error)
            }
          }
        })
        
        console.log('🎭 模型詳情:', {
          meshes: meshCount,
          materials: materialCount,
          children: model.value.children.length
        })
        
        scene.value.add(model.value)
        
        // Setup animations if available - use markRaw for AnimationMixer
        if (gltf.animations && gltf.animations.length > 0) {
          mixer.value = markRaw(new THREE.AnimationMixer(model.value))
          
          gltf.animations.forEach((clip) => {
            try {
              const action = mixer.value.clipAction(clip)
              if (clip.name && clip.name.toLowerCase().includes('idle')) {
                action.play()
              }
            } catch (error) {
              console.warn('Animation setup warning:', error)
            }
          })
          
          console.log('🎭 發現動畫:', gltf.animations.map(a => a.name))
        }
        
        console.log('✅ GLB模型載入成功 - 已加入場景')
        resolve(gltf)
      },
      (progress) => {
        const percent = Math.round((progress.loaded / progress.total) * 100)
        console.log('📥 模型載入進度:', percent + '%')
      },
      (error) => {
        console.error('❌ GLB模型載入失敗:', {
          message: error.message,
          type: error.type,
          url: props.modelUrl
        })
        console.error('完整錯誤物件:', error)
        reject(error)
      }
    )
  })
}

// Animation loop
let animationId = null
function animate() {
  if (!renderer.value || !scene.value || !camera.value) return
  
  animationId = requestAnimationFrame(animate)
  
  try {
    // Update animations
    if (mixer.value) {
      const delta = clock.value.getDelta()
      if (delta > 0 && delta < 0.1) { // Prevent large delta values
        mixer.value.update(delta)
      }
    }
    
    // Add subtle rotation animation
    if (model.value && model.value.position) {
      // Subtle breathing effect
      const time = Date.now() * 0.001
      model.value.position.y = -1 + Math.sin(time * 2) * 0.02
      
      // Slight rotation based on emotion
      if (props.action === 'Talking' && model.value.rotation) {
        model.value.rotation.y += 0.001
      }
    }
    
    renderer.value.render(scene.value, camera.value)
  } catch (error) {
    console.warn('Animation loop warning:', error)
    // Continue rendering despite minor errors
  }
}

// Handle window resize
function handleResize() {
  if (!containerRef.value || !camera.value || !renderer.value) return
  
  const width = containerRef.value.clientWidth
  const height = containerRef.value.clientHeight
  
  camera.value.aspect = width / height
  camera.value.updateProjectionMatrix()
  renderer.value.setSize(width, height)
}

// Watch for emotion/action changes
watch([() => props.emotion, () => props.action], ([newEmotion, newAction]) => {
  if (!model.value || !mixer.value) return
  
  console.log('🎭 表情/動作變化:', { emotion: newEmotion, action: newAction })
  
  // TODO: Implement emotion-based animations
  // This would require specific animation clips in the GLB file
})

onMounted(async () => {
  await initThreeScene()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  
  // Stop animation loop
  if (animationId) {
    cancelAnimationFrame(animationId)
    animationId = null
  }
  
  // Cleanup Three.js resources
  if (mixer.value) {
    mixer.value.stopAllAction()
    mixer.value = null
  }
  
  if (renderer.value) {
    renderer.value.dispose()
    renderer.value.domElement?.remove()
    renderer.value = null
  }
  
  if (scene.value) {
    // Properly dispose scene children
    scene.value.traverse((child) => {
      if (child.geometry) child.geometry.dispose()
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(material => material.dispose())
        } else {
          child.material.dispose()
        }
      }
    })
    scene.value.clear()
    scene.value = null
  }
  
  model.value = null
  camera.value = null
  clock.value = null
})
</script>

<style scoped>
.model-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.three-container {
  width: 100%;
  height: 300px;
  position: relative;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--background-light);
  box-shadow: var(--shadow-lg);
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 10;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--background-light);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-indicator p {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
}

.speaking-indicator {
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 3px;
}

.wave {
  width: 4px;
  height: 15px;
  background: var(--primary-color);
  border-radius: 2px;
  animation: wave 0.6s ease-in-out infinite;
}

.wave:nth-child(2) {
  animation-delay: 0.1s;
}

.wave:nth-child(3) {
  animation-delay: 0.2s;
}

.status-text {
  margin-top: var(--spacing-lg);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  text-align: center;
  font-weight: 500;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes wave {
  0%, 100% {
    height: 10px;
  }
  50% {
    height: 20px;
  }
}

@media (max-width: 768px) {
  .three-container {
    height: 250px;
  }
  
  .loading-indicator {
    font-size: var(--font-size-xs);
  }
  
  .loading-spinner {
    width: 30px;
    height: 30px;
    border-width: 3px;
  }
}
</style>